import { useState } from "react";
import { diffAttributes, type AttributeTracker } from "../../core/attributes";
import { JsonTree } from "../JsonTree";

type Props = {
  tracker: AttributeTracker | undefined;
  contactIds: string[];
  selectedContactId: string | undefined;
  onSelectContact: (contactId: string) => void;
  /** Bumped by the parent whenever the event log changes, to refresh captured snapshots. */
  version: number;
};

export function AttributesPanel({
  tracker,
  contactIds,
  selectedContactId,
  onSelectContact,
  version,
}: Props): React.JSX.Element {
  const [capturing, setCapturing] = useState(false);
  void version;

  if (!tracker) {
    return (
      <p className="empty-state">
        Not connected yet — attributes are unavailable.
      </p>
    );
  }

  const history = selectedContactId ? tracker.history(selectedContactId) : [];
  const latest = history[history.length - 1];

  const captureNow = (): void => {
    if (!selectedContactId) return;
    setCapturing(true);
    tracker.capture(selectedContactId).finally(() => setCapturing(false));
  };

  return (
    <div className="attributes-panel">
      <div className="attributes-controls">
        <select
          aria-label="Select contact"
          value={selectedContactId ?? ""}
          onChange={(e) => onSelectContact(e.target.value)}
        >
          <option value="">(select a contact)</option>
          {contactIds.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={!selectedContactId || capturing}
          onClick={captureNow}
        >
          {capturing ? "Capturing…" : "Capture now"}
        </button>
      </div>

      {!selectedContactId ? (
        <p className="empty-state">Select a contact to view its attributes.</p>
      ) : !latest ? (
        <p className="empty-state">
          No snapshot captured yet for this contact.
        </p>
      ) : (
        <>
          {latest.attributesError ? (
            <div className="attributes-failed" role="alert">
              Attribute query FAILED: {latest.attributesError}
            </div>
          ) : Object.keys(latest.values).length === 0 ? (
            <p className="attributes-empty">
              Query succeeded — this contact has no attributes set.
            </p>
          ) : (
            <table className="attributes-table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(latest.values).map(([k, v]) => (
                  <tr key={k}>
                    <td>{k}</td>
                    <td>
                      <JsonTree value={v} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <h3>Diff history</h3>
          {history.length < 2 ? (
            <p className="empty-state">
              Need at least two snapshots to show a diff.
            </p>
          ) : (
            <ul className="attributes-diff-history">
              {history.slice(1).map((snap, i) => {
                const prev = history[i];
                const delta = diffAttributes(prev?.values, snap.values);
                const hasChange =
                  delta.added.length +
                    delta.changed.length +
                    delta.removed.length >
                  0;
                return (
                  <li key={snap.ts} className="attributes-diff-entry">
                    <span className="attributes-diff-ts">{snap.ts}</span>
                    {delta.added.map((k) => (
                      <span className="diff-added" key={`a-${k}`}>
                        +{k}
                      </span>
                    ))}
                    {delta.changed.map((c) => (
                      <span className="diff-changed" key={`c-${c.k}`}>
                        ~{c.k}
                      </span>
                    ))}
                    {delta.removed.map((k) => (
                      <span className="diff-removed" key={`r-${k}`}>
                        -{k}
                      </span>
                    ))}
                    {!hasChange ? (
                      <span className="diff-none">no change</span>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
